import { PageSizing } from 'components/Layout/PageContentWidth';
import { ContentCard, PageWrapper } from '../styles';
import { TFunction, useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import BillingService from 'services/BillingService';
import LazyLoadList from '__components/Kizen/LazyLoad/LazyLoadList';
import { RowCell, RowWrapper } from './styles';
import { useEndsOfYScroll } from '__components/Kizen/LazyLoad/helpers';
import { Dot } from '__components/Fields/ColorLabel';
import Icon from '__components/Kizen/Icon';
import IconButtonMenu from '__components/Kizen/IconButtonMenu';
import { emptyLongDash } from '__components/Inputs/DateTimeInput/helpers';
import { SortableHeadCell } from '__components/Kizen/Table';
import { Billing } from 'queries/query-keys';
import { formatBillingCurrencyFromString } from '../util';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid':
      return 'var(--color-viz-cat-1-primary)';
    case 'payment_failure_retrying':
      return 'var(--color-viz-cat-7-primary)';
    case 'payment_failure_final':
      return 'var(--color-viz-cat-7-primary)';
    case 'charge_pending':
      return 'var(--color-viz-cat-3-primary)';
    case 'approved':
      return 'var(--color-viz-cat-2-primary)';
  }
};

const getStatusLabel = (status: string, t: TFunction) => {
  switch (status) {
    case 'paid':
      return t('Paid');
    case 'payment_failure_retrying':
      return t('Payment Failed (Retrying)');
    case 'payment_failure_final':
      return t('Payment Failed (Final)');
    case 'charge_pending':
      return t('Charge Pending');
    case 'approved':
      return t('Payment Scheduled');
  }
};

const getActionOptions = (
  invoice: { receipt_file: string; invoice_file: string },
  t: TFunction
) => {
  const actions = [];

  if (invoice.receipt_file) {
    actions.push(
      { value: 'viewReceipt', label: t('View Receipt') },
      { value: 'downloadReceipt', label: t('Download Receipt') }
    );
  }

  if (invoice.invoice_file) {
    actions.push(
      { value: 'viewInvoice', label: t('View Invoice') },
      { value: 'downloadInvoice', label: t('Download Invoice') }
    );
  }

  return actions.sort((a, b) => {
    const isAView = a.value.startsWith('view');
    const isBView = b.value.startsWith('view');
    return isAView === isBView
      ? a.value.localeCompare(b.value)
      : isAView
        ? -1
        : 1;
  });
};

export const BillingInvoicesPage = ({
  setSortOrder,
  sortOrder,
}: {
  setSortOrder: (order: { column: string; direction: string }) => void;
  sortOrder: { column: string; direction: string };
}) => {
  const { t } = useTranslation();
  const scrollContainer = useRef<HTMLDivElement>(null);

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: Billing.INVOICE(sortOrder),
    queryFn: async ({ pageParam = 1 }) => {
      const data = await BillingService.getInvoicesPage({
        page: pageParam,
        pageSize: 20,
        ordering: `${sortOrder.direction === 'desc' ? '-' : ''}${sortOrder.column}`,
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next
        ? parseInt(new URL(lastPage.next).searchParams.get('page') || '1')
        : undefined;
    },
    getPreviousPageParam: (previousPage) => {
      return previousPage?.previous
        ? parseInt(
            new URL(previousPage.previous).searchParams.get('page') || '1'
          )
        : undefined;
    },
    retry: false,
    throwOnError: false,
    enabled: true,
  });

  const onScrollThreshold = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const invoices = useMemo(() => {
    const res =
      invoicesData?.pages.reduce((acc, page) => {
        return acc.concat(page.results);
      }, []) ?? [];

    return res;
  }, [invoicesData]);

  const [scrolledTop, scrolledBottom] = useEndsOfYScroll(scrollContainer, [
    invoices,
  ]);

  const isScrollable = !scrolledTop || !scrolledBottom;

  const chooseOptionHandler = useCallback(
    (invoiceItem: any) =>
      ({ value }: { value: string }) => {
        const { receipt_file, invoice_file } = invoiceItem;
        const handleDownload = (file: string, fileName: string) => {
          const link = document.createElement('a');
          link.href = `${file}?disposition=attachment`;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };

        switch (value) {
          case 'downloadReceipt': {
            handleDownload(receipt_file, 'receipt.pdf');
            break;
          }
          case 'viewReceipt': {
            window.open(receipt_file, '_blank');
            break;
          }
          case 'downloadInvoice': {
            handleDownload(invoice_file, 'invoice.pdf');
            break;
          }
          case 'viewInvoice': {
            window.open(invoice_file, '_blank');
            break;
          }
        }
      },
    []
  );

  const sortCellData = useMemo(
    () => ({
      meta: {
        sort: sortOrder,
        onSort: (column: string, direction: string) => {
          setSortOrder({
            column,
            direction,
          });
        },
      },
    }),
    [sortOrder, setSortOrder]
  );

  return (
    <PageSizing>
      <PageWrapper>
        <div className="w-full">
          <div>
            <ContentCard padding={0} minWidth={1200}>
              <div className="p-[20px]">
                <Typography variant="header" size="2xl" weight="semibold">
                  {t('Invoices')}
                </Typography>
              </div>
              <div className="w-full h-[600px] pb-[35px]">
                <RowWrapper header useScrollbarPadding={isScrollable}>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Invoice Date')}
                      hasSorting
                      width="150px"
                      numeric
                      truncate
                      data={sortCellData}
                      column={{
                        id: 'created',
                      }}
                    />
                  </RowCell>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Due/Charge Date')}
                      hasSorting
                      width="150px"
                      numeric
                      truncate
                      data={sortCellData}
                      column={{
                        id: 'paid_at',
                      }}
                    />
                  </RowCell>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Receipt ID')}
                      data={sortCellData}
                      width="150px"
                      truncate
                      column={{
                        id: 'receipt_id',
                      }}
                      hasSorting={false}
                    />
                  </RowCell>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Type')}
                      hasSorting
                      truncate
                      width="150px"
                      data={sortCellData}
                      column={{
                        id: 'invoice_type',
                      }}
                    />
                  </RowCell>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Status')}
                      hasSorting
                      truncate
                      width="200px"
                      data={sortCellData}
                      column={{
                        id: 'status',
                      }}
                    />
                  </RowCell>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Invoice Total')}
                      hasSorting
                      truncate
                      width="150px"
                      data={sortCellData}
                      numeric
                      column={{
                        id: 'total',
                      }}
                    />
                  </RowCell>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Payment Method')}
                      data={sortCellData}
                      truncate
                      numeric
                      column={{
                        id: 'payment_method',
                      }}
                      minWidth="120px"
                      hasSorting={false}
                    />
                  </RowCell>
                  <RowCell>
                    <SortableHeadCell
                      label={t('Actions')}
                      x
                      data={sortCellData}
                      truncate
                      numeric
                      width="60px"
                      column={{
                        id: 'payment_method',
                      }}
                      hasSorting={false}
                    />
                  </RowCell>
                </RowWrapper>
                <LazyLoadList
                  data={invoices}
                  onScrollThreshold={onScrollThreshold}
                  fetchingNextPage={isFetchingNextPage}
                  loading={invoicesLoading}
                  scrollContainerRef={scrollContainer}
                  className="kds-expanding-scrollbar"
                  kdsCompat
                >
                  {invoices.map((invoice: any) => {
                    return (
                      <RowWrapper key={invoice.created}>
                        <RowCell>
                          <Typography truncate>
                            {format(new Date(invoice.created), 'MM/dd/yyyy')}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate className="w-full">
                            {invoice.paid_at
                              ? format(new Date(invoice.paid_at), 'MM/dd/yyyy')
                              : emptyLongDash}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate className="w-full">
                            {invoice.stripe_receipt_number
                              ? `KZN-${invoice.stripe_receipt_number}`
                              : invoice.stripe_invoice_number
                                ? `KZN-${invoice.stripe_invoice_number}`
                                : emptyLongDash}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate className="w-full">
                            {invoice.invoice_type}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Dot color={getStatusColor(invoice.status)} />
                          <Typography truncate className="w-full">
                            {getStatusLabel(invoice.status, t)}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate className="w-full">
                            {invoice.total
                              ? formatBillingCurrencyFromString(invoice.total)
                              : emptyLongDash}
                          </Typography>
                        </RowCell>
                        <RowCell>
                          <Typography truncate className="w-full">
                            {invoice.payment_method_info.last4
                              ? `${t('Credit Card')} - ${invoice.payment_method_info.last4}`
                              : emptyLongDash}
                          </Typography>
                        </RowCell>
                        <RowCell right>
                          {invoice.receipt_file || invoice.invoice_file ? (
                            <IconButtonMenu
                              title={t('Perform Action')}
                              position="right"
                              sizing="dense"
                              options={getActionOptions(invoice, t)}
                              onChange={chooseOptionHandler(invoice)}
                              restrictCloseHandler
                              color="var(--color-background-dark)"
                            >
                              <Icon icon="three-dot" />
                            </IconButtonMenu>
                          ) : null}
                        </RowCell>
                      </RowWrapper>
                    );
                  })}
                </LazyLoadList>
              </div>
            </ContentCard>
          </div>
        </div>
      </PageWrapper>
    </PageSizing>
  );
};
